<template>
  <div class="login-container">
    <div class="login-split">
      <!-- Left: image panel -->
      <aside class="login-image" aria-hidden="true">
        <div class="image-copy">
          <h1 class="brand">Welcome</h1>
          <p class="tag">Login with Email</p>
        </div>
      </aside>

      <!-- Right: form panel -->
      <section class="login-form">
        <div class="logo-container">
          <img src="https://res.cloudinary.com/dzrldiajp/image/upload/f_auto,q_auto,w_150/v1764022549/uedqwwovgrlghzczewmv.png" alt="Company Logo" class="login-logo"/>
        </div>
        <form @submit.prevent="onSubmit">
          <h2>Login</h2>
          <div class="input-group">
            <label for="username">Username</label>
            <input id="username" type="text" placeholder="Type your username (email)" v-model="username" required/>
          </div>
          <div class="input-group password-group">
            <label for="password">Password</label>
            <input :type="showPassword ? 'text' : 'password'" id="password" placeholder="Type your password" v-model="password" required/>
            <button type="button" class="toggle-password" :aria-pressed="showPassword.toString()" @click="togglePassword" :title="showPassword ? 'Hide password' : 'Show password'">
              {{ showPassword ? 'Hide' : 'Show' }}
            </button>
          </div>
          <div class="actions">
            <a href="#" @click.prevent="forgotPassword">Forgot password?</a>
          </div>
          <button type="submit" class="btn login-btn">Login</button>
        </form>
        <div class="foot-note">
          <span>⚡ L⚡t‑ning Analytics</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      username: '',
      password: '',
      showPassword: false
    };
  },
  methods: {
    onSubmit() {
      this.$utils.showLoading();
      return this.$store.dispatch(`franchiseLogin`, { email: this.username, password: this.password })
        .then(() => this.$router.push('dashboard'))
    },
    forgotPassword() {
      console.log('Forgot password clicked');
    },
    /**
     * Toggle visibility of the password field.
     * UI-only; does not alter authentication logic.
     */
    togglePassword() {
      this.showPassword = !this.showPassword;
    }
  }
};
</script>

<style scoped>
/* Page wrapper */
.login-container {
  /* Use dynamic viewport units to keep the card centered on mobile URL bars */
  min-height: 100vh; /* fallback */
  min-height: 100dvh; /* modern browsers */
  display: grid;
  place-items: center;
  background: var(--bg-gradient);
  font-family: var(--font-family);
  padding: 24px;
}

/* Split card container */
.login-split {
  width: min(1100px, 100%);
  min-height: 520px;
  background: var(--card-bg);
  color: var(--text-primary);
  border: var(--card-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow);
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  overflow: hidden;
  /* Ensure robust horizontal centering even if parent grid alignment is overridden */
  margin: 0 auto;
  justify-self: center;
}

/* Left image panel */
.login-image {
  position: relative;
  background-image: var(--login-hero-image);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.login-image::after {
  content: "";
  position: absolute; inset: 0;
  background: var(--login-overlay);
}
.image-copy {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  padding: 24px;
  color: var(--text-inverse);
}
@media (max-width: 960px) {
  .image-copy {
    align-items: flex-start;
    justify-content: flex-end;
    text-align: left;
    padding: 24px;
  }
}
.image-copy .brand { margin: 0 0 6px 0; font-size: 28px; font-weight: var(--fw-heading); }
.image-copy .tag   { margin: 0; font-size: 14px; opacity: 0.9; }

/* Right form panel */
.login-form { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px; }
/* Subtle vertical divider between image and form on desktop */
.login-form::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 1px; background: var(--grid-lines); }
@media (max-width: 960px) { .login-form { padding: 16px; } .login-form::before { display: none; } }
form {
  width: 100%;
  max-width: 440px;
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  text-align: left;
}

@media (max-width: 960px) {
  .login-split { grid-template-columns: 1fr; min-height: unset; }
  .login-image { display: none; }
}
@media (max-width: 480px) {
  .login-container { padding: 16px; }
}

h2 {
  margin-bottom: var(--space-4, 16px);
  font-size: 26px;
  font-weight: var(--fw-heading, 600);
  color: var(--text-primary);
}

.input-group { margin-bottom: var(--space-4, 16px); text-align: left; position: relative; }
label { display: block; font-size: 13px; color: var(--text-secondary); margin-bottom: 6px; }
input {
  width: 100%; height: 38px;
  padding: 8px 10px;
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-input);
  font-size: 14px;
  background: var(--bg);
  color: var(--text-primary);
}
.password-group input { padding-right: 72px; }
.toggle-password {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1;
  color: var(--text-primary);
  background: var(--card-bg);
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-button);
  cursor: pointer;
  transition: var(--transition);
}
.toggle-password:hover { filter: brightness(1.05); }
.toggle-password:focus { outline: 2px solid var(--accent-secondary); outline-offset: 1px; }
.actions { display: flex; justify-content: flex-end; margin-bottom: var(--space-4, 16px); }
.actions a { font-size: 12px; color: var(--accent-primary); text-decoration: none; }
.actions a:hover { text-decoration: underline; }

.btn { display: inline-block; width: 100%; padding: 0.5rem; border: none; border-radius: var(--radius-button); font-size: 14px; color: #fff; background: var(--accent-primary); cursor: pointer; transition: var(--transition); }
.btn:hover { filter: brightness(1.05); }

.logo-container {
  margin-bottom: 1.5rem;
  text-align: center;
}

.login-logo {
  max-width: 150px;
  height: auto;
}

.foot-note { margin-top: auto; padding-top: 12px; text-align: center; color: var(--text-secondary); font-size: 12px; }
</style>
